import { useState } from 'react';
import { Alert, Button, ConfirmActionModal } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { isApiError } from '@/services/api';
import type { ConfigSetting } from '@/features/products/types/products.types';
import { ProductAddOnDataTable } from './ProductAddOnDataTable/ProductAddOnDataTable';
import { AddOnAssignmentForm } from './AddOnAssignmentForm';
import { useProductAddOnsQuery } from '../hooks/useProductAddOnsQuery';
import { useProductAddOnMutations } from '../hooks/useProductAddOnMutations';
import type { AssignAddOnPayload, UpdateAddOnAssignmentPayload } from '../services/productAddons';

interface Props {
    productId: string;
    configSettings?: ConfigSetting[] | null;
}

export function ProductAddOnPanel({ productId, configSettings }: Props) {
    const { data = [], isLoading } = useProductAddOnsQuery(productId);
    const { assignMutation, updateMutation, detachMutation } = useProductAddOnMutations(productId);
    const [showAssignForm, setShowAssignForm] = useState(false);
    const [assignError, setAssignError] = useState<string | null>(null);
    const [inlineError, setInlineError] = useState<string | null>(null);
    const [detachTargetId, setDetachTargetId] = useState<string | null>(null);

    const assignedIds = data.map((row) => row.id);

    const handleAssign = (payload: AssignAddOnPayload) => {
        assignMutation.mutate(payload, {
            onSuccess: () => {
                setShowAssignForm(false);
                setAssignError(null);
            },
            onError: (err) => {
                setAssignError(isApiError(err) ? err.message : 'Failed to assign optional extra.');
            },
        });
    };

    // Inline edit (row-level) — overrides are edited directly in the data table.
    const handleInlineSave = (addonId: string, payload: UpdateAddOnAssignmentPayload) => {
        setInlineError(null);
        // Return the promise so the inline editor closes only on success; on
        // failure, surface the error and rethrow so the row stays open with the
        // operator's draft intact.
        return updateMutation.mutateAsync({ addonId, payload }).catch((err) => {
            setInlineError(isApiError(err) ? err.message : 'Failed to update override.');
            throw err;
        });
    };

    const handleDetach = (addonId: string) => {
        setDetachTargetId(addonId);
    };

    const handleConfirmDetach = () => {
        if (detachTargetId) detachMutation.mutate(detachTargetId);
        setDetachTargetId(null);
    };

    if (isLoading) return <p className="text-sm text-mp-muted">Loading optional extras…</p>;

    return (
        <div className="space-y-6">
            {inlineError && <Alert variant="error">{inlineError}</Alert>}

            {data.length === 0 ? (
                <p className="text-sm text-mp-muted">No optional extras assigned.</p>
            ) : (
                <ProductAddOnDataTable
                    data={data}
                    configSettings={configSettings}
                    onSave={handleInlineSave}
                    onDetach={handleDetach}
                />
            )}

            <PermissionGuard permission={{ resource: 'smr', action: 'product-addon.add' }}>
                <div className="border-t border-mp-border pt-6 max-w-2xl">
                    {showAssignForm ? (
                        <>
                            <h3 className="font-semibold mb-3">Assign Optional Extra</h3>
                            <AddOnAssignmentForm
                                excludeIds={assignedIds}
                                configSettings={configSettings}
                                onSubmit={handleAssign}
                                isLoading={assignMutation.isPending}
                                error={assignError}
                                onCancel={() => {
                                    setShowAssignForm(false);
                                    setAssignError(null);
                                }}
                            />
                        </>
                    ) : (
                        <div>
                            <h3 className="font-semibold mb-2">Assign Optional Extra</h3>
                            <div className="flex items-start gap-4 justify-between">
                                <p className="text-sm text-mp-muted">
                                    Assign an optional extra to this product.
                                </p>
                                <Button
                                    size="sm"
                                    variant="primary"
                                    type="button"
                                    className="whitespace-nowrap"
                                    onClick={() => {
                                        setAssignError(null);
                                        setShowAssignForm(true);
                                    }}
                                >
                                    Assign Optional Extra
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </PermissionGuard>

            <ConfirmActionModal
                isOpen={detachTargetId !== null}
                title="Remove optional extra"
                message="Remove this optional extra from the product?"
                confirmLabel="Remove"
                confirmVariant="danger"
                isPending={detachMutation.isPending}
                onConfirm={handleConfirmDetach}
                onClose={() => setDetachTargetId(null)}
            />
        </div>
    );
}
